import { ExternalLink, Target } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs";
import { useRef, useState } from "react";

import { Button } from "../ui/button";
import { VideoEmbed } from "../ui/video-embed";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { getDemoLeaderboard } from "@/mock/demo";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

interface Video {
  title: string;
  url: string;
  views: number;
  likes: number;
  platform: string;
  postDate: number;
}

interface CreatorStats {
  creatorId: string;
  name: string;
  avatarUrl: string;
  totalViews: number;
  totalLikes: number;
  totalVideos: number;
  videos: Video[];
}

const getRankingColor = (rank: number) => {
  if (rank === 1) return "text-yellow-600 font-bold";
  if (rank === 2) return "text-gray-400 font-bold";
  if (rank === 3) return "text-amber-600 font-bold";
  return "text-primary/60";
};

const getRankingEmoji = (rank: number) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
};

export function CreatorLeaderboard() {
  const [selectedCreator, setSelectedCreator] = useState<CreatorStats | null>(
    null,
  );
  const { contractId, creatorId } = useAccount();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("current");

  // Demo mode is only for founders, not creators - disable this component in demo mode
  const isDemo = false; // Creator leaderboard not available in founder demo mode

  // Get current user to check if they're a UGC Accelerator member
  const { data: currentUser } = useQuery({
    ...convexQuery(api.app.getCurrentUser, {}),
  });

  const isUGCAcceleratorMember =
    currentUser?.creator?.isUGCAcceleratorMember ?? false;

  // Current campaign leaderboard
  const { data: leaderboardData } = useQuery({
    ...convexQuery(api.videos.getLeaderboardData, { contractId: contractId }),
    enabled: !!contractId,
  });

  // UGC Accelerator leaderboard (all-time)
  const { data: ugcAcceleratorLeaderboardData } = useQuery({
    ...convexQuery(api.videos.getUGCAcceleratorLeaderboardData, {}),
    enabled: isUGCAcceleratorMember && !!contractId,
  });

  // Use demo data when in demo mode
  const effectiveLeaderboardData = isDemo
    ? getDemoLeaderboard()
    : leaderboardData;

  // Determine which leaderboard to show based on active tab
  const displayedLeaderboardData =
    activeTab === "ugc" && isUGCAcceleratorMember && contractId
      ? ugcAcceleratorLeaderboardData
      : effectiveLeaderboardData;

  const currentUserRef = useRef<HTMLDivElement>(null);

  const handleCreatorClick = (creator: CreatorStats) => {
    setSelectedCreator(creator);
    setIsSheetOpen(true);
  };

  const scrollToCurrentUser = () => {
    if (currentUserRef.current) {
      currentUserRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  // Find current user's position in leaderboard
  const currentUserPosition = displayedLeaderboardData?.findIndex(
    (creator) => creator.creatorId === creatorId,
  );
  const isCurrentUserOnLeaderboard =
    currentUserPosition !== undefined && currentUserPosition >= 0;

  // Show loading if we need contractId but don't have it, or if data is not loaded
  const isLoading =
    activeTab === "current" &&
    !displayedLeaderboardData &&
    !!contractId &&
    !isDemo;

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  // Don't show leaderboard if no data and not loading
  if (!displayedLeaderboardData || displayedLeaderboardData.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {activeTab === "ugc"
              ? "No UGC Accelerator members have posted videos yet."
              : "Post 1 video to get on the leaderboard!"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4 p-6">
        {/* Tabs for UGC Accelerator members */}
        {isUGCAcceleratorMember && (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start">
              <TabsTrigger value="current">Current Campaign</TabsTrigger>
              <TabsTrigger value="ugc">UGC Accelerator</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Header with Jump to Me button */}
        <div className="flex items-center justify-between">
          <div className="invisible w-0"></div>
          {creatorId && (
            <div>
              {isCurrentUserOnLeaderboard ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={scrollToCurrentUser}
                  className="flex items-center gap-2"
                >
                  <Target className="h-4 w-4" />
                  Jump to Me (#{(currentUserPosition ?? 0) + 1})
                </Button>
              ) : (
                <div className="text-center">
                  <p className="text-sm text-muted-foreground">
                    Post 1 video to get on the leaderboard!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {displayedLeaderboardData.map((creator, index) => {
            const rank = index + 1;
            const isCurrentUser = creator.creatorId === creatorId;

            return (
              <div
                key={creator.creatorId}
                ref={isCurrentUser ? currentUserRef : null}
                className={`flex cursor-pointer items-center justify-between rounded-lg border p-4 transition-colors ${
                  isCurrentUser
                    ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                    : "border-border bg-card hover:bg-secondary/50"
                }`}
                onClick={() => handleCreatorClick(creator)}
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`text-2xl font-bold min-w-[60px] ${getRankingColor(rank)}`}
                  >
                    {getRankingEmoji(rank)}
                  </div>
                  <img
                    src={creator.avatarUrl}
                    alt={creator.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-primary">
                        {creator.name}
                      </h3>
                      {isCurrentUser && (
                        <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full font-medium">
                          You
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-primary/60">
                      {creator.totalVideos} videos
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-primary">
                    {new Intl.NumberFormat().format(creator.totalViews)} views
                  </p>
                  <p className="text-sm text-primary/60">
                    {new Intl.NumberFormat().format(creator.totalLikes)} likes
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <SheetContent className="w-[400px] sm:w-[500px] p-0">
          {selectedCreator && (
            <>
              <SheetHeader className="p-6 border-b border-border">
                <div className="flex items-center gap-4">
                  <img
                    src={selectedCreator.avatarUrl}
                    alt={selectedCreator.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div className="flex-1">
                    <SheetTitle className="text-xl font-semibold text-primary">
                      {selectedCreator.name}
                    </SheetTitle>
                    <div className="flex gap-4 mt-2 text-sm text-primary/60">
                      <span>{selectedCreator.totalVideos} videos</span>
                      <span>
                        {new Intl.NumberFormat().format(
                          selectedCreator.totalViews,
                        )}{" "}
                        total views
                      </span>
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(100vh-120px)]">
                {selectedCreator.videos.map((video: Video, index: number) => (
                  <div key={index} className="space-y-3">
                    <VideoEmbed url={video.url} platform={video.platform} />

                    <div className="space-y-2">
                      <h3 className="font-medium text-primary line-clamp-2">
                        {video.title || "Untitled Video"}
                      </h3>

                      <div className="flex items-center justify-between text-sm text-primary/60">
                        <span>
                          {new Intl.NumberFormat().format(video.views)} views
                        </span>
                        <span>
                          {new Intl.NumberFormat().format(video.likes)} likes
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-xs text-primary/40">
                        <span className="capitalize">{video.platform}</span>
                        <span>
                          {new Date(video.postDate).toLocaleDateString()}
                        </span>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => window.open(video.url, "_blank")}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        View Original
                      </Button>
                    </div>

                    {index < selectedCreator.videos.length - 1 && (
                      <hr className="border-border" />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
